/**
 * Fixed-Asset · Make / Buy / Hire Simulator.
 *
 * User inputs the three option costs · discount rate · life · tax rate, we hit
 * POST /reports/fixed-asset/simulate and render NPV/IRR/payback per option,
 * a sensitivity panel (±10% cost), and a save-scenario / download-PDF workflow.
 */
import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Cog, ShoppingBag, Handshake, RefreshCw, FileDown, Save, Trophy } from "lucide-react";
import { downloadBlob } from "@/lib/download";

const fmtINR = (v) => {
  if (v === null || v === undefined) return "-";
  const a = Math.abs(Number(v));
  if (a >= 1e7) return "₹" + (v / 1e7).toFixed(2) + "Cr";
  if (a >= 1e5) return "₹" + (v / 1e5).toFixed(2) + "L";
  return "₹" + Number(v).toLocaleString("en-IN");
};

const OPT_META = {
  make: { icon: Cog, label: "MAKE", tone: "text-blue-600 border-blue-500/40" },
  buy:  { icon: ShoppingBag, label: "BUY", tone: "text-purple-600 border-purple-500/40" },
  hire: { icon: Handshake, label: "HIRE", tone: "text-amber-600 border-amber-500/40" },
};

export default function FixedAssetSimulator() {
  const [assetName, setAssetName] = useState("New CNC Machine");
  const [inputs, setInputs] = useState({
    life_years: 5,
    discount_rate_pct: 12,
    tax_rate_pct: 25.17,
    revenue_uplift_per_year: 2500000,
    make: { capex_diy: 3500000, opex_per_year: 250000, salvage_pct: 10 },
    buy:  { capex_purchase: 4000000, opex_per_year: 180000, salvage_pct: 15 },
    hire: { monthly_rent: 65000, deposit: 200000, opex_per_year: 40000 },
  });
  const [sim, setSim] = useState(null);
  const [loading, setLoading] = useState(false);
  const [pdfBusy, setPdfBusy] = useState(false);
  const [scenarios, setScenarios] = useState([]);

  const loadScenarios = () => api.get("/reports/fixed-asset/scenarios")
    .then(r => setScenarios(r.data || []))
    .catch(() => {});
  useEffect(() => { loadScenarios(); }, []);

  const run = async () => {
    setLoading(true);
    try {
      const r = await api.post("/reports/fixed-asset/simulate", inputs);
      setSim(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Simulation failed"); }
    setLoading(false);
  };
  useEffect(() => { run(); }, []);  // eslint-disable-line

  const downloadPDF = async () => {
    setPdfBusy(true);
    try {
      const ok = await downloadBlob("/reports/fixed-asset/simulate.pdf", `FA_${assetName.replace(/\s+/g,"_")}.pdf`,
        { method: "POST", data: { inputs, asset_name: assetName } });
      if (ok) toast.success("Simulator PDF downloaded");
    } catch (e) { toast.error(e?.response?.data?.detail || "PDF failed"); }
    setPdfBusy(false);
  };

  const saveScenario = async () => {
    if (!sim) return;
    try {
      await api.post("/reports/fixed-asset/scenarios", {
        asset_name: assetName, inputs, result: sim.recommendation, notes: "",
      });
      toast.success("Scenario saved");
      loadScenarios();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };

  const setNested = (option, key, val) => setInputs(s => ({
    ...s, [option]: { ...s[option], [key]: Number(val) || 0 }
  }));

  return (
    <div className="p-6 lg:p-8 space-y-4" data-testid="fa-simulator-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-accent">// Fixed Asset · Make · Buy · Hire</div>
          <h1 className="text-3xl font-bold mt-1 font-heading">Capital Allocation Simulator</h1>
          <div className="text-sm text-muted-foreground mt-1 leading-relaxed max-w-3xl">
            Compare NPV · IRR · payback across the three fundamental capital
            allocation options. Sensitivity toggles show what happens at ±10%
            cost. Save scenarios per asset · download the banker-ready PDF.
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Input value={assetName} onChange={e => setAssetName(e.target.value)}
            placeholder="Asset name" className="h-9 w-56 text-sm" data-testid="fa-asset-name" />
          <Button onClick={saveScenario} disabled={!sim} variant="outline" className="rounded-sm gap-1.5" data-testid="fa-save">
            <Save className="w-4 h-4" /> Save
          </Button>
          <Button onClick={downloadPDF} disabled={pdfBusy || !sim}
            className="rounded-sm bg-accent hover:bg-accent/90 text-white gap-1.5" data-testid="fa-pdf">
            {pdfBusy ? <RefreshCw className="w-4 h-4 animate-spin" /> : <FileDown className="w-4 h-4" />}
            PDF
          </Button>
        </div>
      </div>

      {/* Global inputs */}
      <div className="card-sharp p-4 space-y-3" data-testid="fa-globals">
        <div className="text-[10px] font-mono uppercase text-accent">// Common assumptions</div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div><Label className="text-[10px] uppercase font-mono">Life (years)</Label>
            <Input type="number" value={inputs.life_years} onChange={e => setInputs(s => ({...s, life_years: Number(e.target.value) || 1}))} className="h-8 mt-1 text-right tabular-nums" data-testid="fa-life" /></div>
          <div><Label className="text-[10px] uppercase font-mono">Discount rate %</Label>
            <Input type="number" step="0.1" value={inputs.discount_rate_pct} onChange={e => setInputs(s => ({...s, discount_rate_pct: Number(e.target.value)}))} className="h-8 mt-1 text-right tabular-nums" data-testid="fa-discount" /></div>
          <div><Label className="text-[10px] uppercase font-mono">Tax rate %</Label>
            <Input type="number" step="0.01" value={inputs.tax_rate_pct} onChange={e => setInputs(s => ({...s, tax_rate_pct: Number(e.target.value)}))} className="h-8 mt-1 text-right tabular-nums" data-testid="fa-tax" /></div>
          <div><Label className="text-[10px] uppercase font-mono">Revenue uplift ₹/year</Label>
            <Input type="number" value={inputs.revenue_uplift_per_year} onChange={e => setInputs(s => ({...s, revenue_uplift_per_year: Number(e.target.value)}))} className="h-8 mt-1 text-right tabular-nums" data-testid="fa-revenue" /></div>
        </div>
      </div>

      {/* Per-option inputs */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {[
          ["make", "CAPEX (DIY)", "capex_diy"],
          ["buy",  "CAPEX (purchase)", "capex_purchase"],
          ["hire", "Monthly rent", "monthly_rent"],
        ].map(([opt, primaryLbl, primaryKey]) => {
          const M = OPT_META[opt];
          const Icon = M.icon;
          return (
            <div key={opt} className={`card-sharp p-4 border-t-4 ${M.tone}`} data-testid={`fa-opt-${opt}`}>
              <div className="flex items-center gap-2 mb-3">
                <Icon className="w-4 h-4" />
                <div className="text-sm font-bold font-mono tracking-widest">{M.label}</div>
              </div>
              <div className="space-y-2">
                <div>
                  <Label className="text-[10px] uppercase font-mono">{primaryLbl}</Label>
                  <Input type="number" value={inputs[opt][primaryKey]}
                    onChange={e => setNested(opt, primaryKey, e.target.value)}
                    className="h-8 mt-1 text-right tabular-nums font-mono" data-testid={`fa-${opt}-primary`} />
                </div>
                {opt === "hire" && (
                  <div><Label className="text-[10px] uppercase font-mono">Deposit</Label>
                    <Input type="number" value={inputs.hire.deposit}
                      onChange={e => setNested("hire", "deposit", e.target.value)}
                      className="h-8 mt-1 text-right tabular-nums font-mono" data-testid="fa-hire-deposit" />
                  </div>
                )}
                <div>
                  <Label className="text-[10px] uppercase font-mono">Opex/year</Label>
                  <Input type="number" value={inputs[opt].opex_per_year}
                    onChange={e => setNested(opt, "opex_per_year", e.target.value)}
                    className="h-8 mt-1 text-right tabular-nums font-mono" data-testid={`fa-${opt}-opex`} />
                </div>
                {opt !== "hire" && (
                  <div><Label className="text-[10px] uppercase font-mono">Salvage %</Label>
                    <Input type="number" step="0.1" value={inputs[opt].salvage_pct}
                      onChange={e => setNested(opt, "salvage_pct", e.target.value)}
                      className="h-8 mt-1 text-right tabular-nums font-mono" data-testid={`fa-${opt}-salvage`} />
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <div className="flex justify-end">
        <Button onClick={run} disabled={loading}
          className="rounded-sm bg-accent hover:bg-accent/90 text-white gap-1.5" data-testid="fa-run">
          {loading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
          Re-run simulation
        </Button>
      </div>

      {/* Winner banner */}
      {sim && (
        <div className="card-sharp p-4 border-l-4 border-l-emerald-500 bg-emerald-500/5 flex items-start gap-3" data-testid="fa-winner">
          <Trophy className="w-6 h-6 text-emerald-600 shrink-0 mt-0.5" />
          <div className="flex-1">
            <div className="text-[10px] font-mono uppercase tracking-widest text-emerald-600">// Winner</div>
            <div className="text-lg font-bold mt-0.5">{sim.recommendation.option.toUpperCase()}</div>
            <div className="text-sm text-muted-foreground mt-1">{sim.recommendation.rationale}</div>
            <div className="flex items-center gap-4 mt-2 font-mono text-xs">
              <span>NPV <b className="text-emerald-600">{fmtINR(sim.recommendation.npv)}</b></span>
              <span>IRR <b>{sim.recommendation.irr_pct != null ? `${sim.recommendation.irr_pct}%` : "-"}</b></span>
              <span>Payback <b>{sim.recommendation.payback_years != null ? `${sim.recommendation.payback_years}y` : "-"}</b></span>
            </div>
          </div>
        </div>
      )}

      {/* Comparison table */}
      {sim && (
        <div className="card-sharp overflow-hidden" data-testid="fa-comparison">
          <div className="p-3 border-b bg-muted/30 text-sm font-semibold">Option comparison</div>
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-[10px] font-mono uppercase tracking-widest text-muted-foreground">
              <tr>
                <th className="text-left p-2.5">Metric</th>
                <th className="text-center p-2.5">Make</th>
                <th className="text-center p-2.5">Buy</th>
                <th className="text-center p-2.5">Hire</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {[
                ["NPV @ discount rate", (o) => fmtINR(o.npv)],
                ["IRR %", (o) => o.irr_pct != null ? `${o.irr_pct}%` : "-"],
                ["Payback (years)", (o) => o.payback_years != null ? `${o.payback_years}` : "-"],
                ["Total outflows", (o) => fmtINR(o.total_undiscounted_outflows)],
                ["NPV @ -10% cost", (o) => fmtINR(o.sensitivity.minus_10pct)],
                ["NPV @ +10% cost", (o) => fmtINR(o.sensitivity.plus_10pct)],
              ].map(([label, fn], idx) => (
                <tr key={idx}>
                  <td className="p-2.5 font-medium">{label}</td>
                  {["make","buy","hire"].map(opt => (
                    <td key={opt} className={`p-2.5 text-center font-mono ${opt === sim.recommendation.option ? "bg-emerald-500/5 text-emerald-600 font-bold" : ""}`}>
                      {fn(sim.options[opt])}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Year-by-year cash flow */}
      {sim && (
        <div className="card-sharp overflow-hidden" data-testid="fa-cashflows">
          <div className="p-3 border-b bg-muted/30 text-sm font-semibold">Year-by-year cash flow (undiscounted)</div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-muted/50 text-[10px] font-mono uppercase text-muted-foreground">
                <tr>
                  <th className="text-left p-2">Option</th>
                  {Array.from({length: sim.life_years + 1}, (_, i) => (
                    <th key={i} className="text-right p-2">Y{i}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {["make","buy","hire"].map(opt => (
                  <tr key={opt} className={opt === sim.recommendation.option ? "bg-emerald-500/5" : ""}>
                    <td className="p-2 font-mono font-bold uppercase">{opt}</td>
                    {sim.options[opt].cash_flows.map((cf, i) => (
                      <td key={i} className={`p-2 text-right font-mono ${cf < 0 ? "text-red-500" : "text-emerald-600"}`}>{fmtINR(cf)}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Saved scenarios */}
      {scenarios.length > 0 && (
        <div className="card-sharp overflow-hidden" data-testid="fa-scenarios">
          <div className="p-3 border-b bg-muted/30 text-sm font-semibold">Saved scenarios · {scenarios.length}</div>
          <table className="w-full text-xs">
            <thead className="bg-muted/50 text-[10px] font-mono uppercase text-muted-foreground">
              <tr><th className="text-left p-2">Asset</th><th className="text-left p-2">Winner</th><th className="text-right p-2">NPV</th><th className="text-right p-2">Saved</th></tr>
            </thead>
            <tbody className="divide-y divide-border">
              {scenarios.map(sc => (
                <tr key={sc.id}>
                  <td className="p-2 font-medium">{sc.asset_name}</td>
                  <td className="p-2 font-mono uppercase font-bold text-emerald-600">{sc.result?.option || "—"}</td>
                  <td className="p-2 text-right font-mono">{fmtINR(sc.result?.npv || 0)}</td>
                  <td className="p-2 text-right text-muted-foreground text-[10px]">{sc.created_at?.slice(0,10)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
